import React, { useMemo, useEffect, useState, useRef } from 'react';
import { ImageBackground, Text, View, BackHandler, Pressable } from 'react-native';
// eslint-disable-next-line import/no-extraneous-dependencies
import { IAudioTrack, ITextTrack } from '@24i/player-base';
import { ConfirmationModal, Icon, useConfirmationModal } from 'LocalGluons';
import { Device } from 'DeviceUtils';
import type { PlayerData } from 'types';
import { PlayerManager } from '@24i/react-native-player';
import { isLive } from 'LocalGluons/components/PlayerContainer/hooks/useAdvertisements';
import { useStore } from 'Store';
import storeActions from 'Store/storeActions';
import appManager, { logEvent } from 'AppManager';
import { useChromecastContext } from 'LocalGluons/components/ChromecastContext';
import { t } from 'Locale';
import { isUserFreemium } from 'Utils/userUtils';
import styles from './styles';
import TextIcon from '../../../TextIcon';
import { StyleMap } from '../../../../../types';
import { STREAM_TYPE } from '../../../../../options';
import useContinueWatching from '../../../PlayerContainer/hooks/useContinueWatching';
import AnimatedCircle from '../LowLatencyModal/Animation/Circle';
import CastingButton from '../Casting/CastingButton';

interface PlayerHeaderProps {
    onBack: () => void;
    channelLogoUrl?: string;
    title: string;
    streamType?: string;
    style?: StyleMap[] | StyleMap;
    onMiniPlayerPress?: () => void;
    onInfoPress?: () => void;
    onTvGuidePress?: () => void;
    onLiveTvPress?: () => void;
    // onLanguageSelectionPress?: () => void;
    onLanguagePress?: () => void;
    textTracks?: ITextTrack[];
    audioTracks?: IAudioTrack[];
    selectedAudioTrack?: IAudioTrack | null;
    item?: PlayerData['item'];
    currentTime?: number;
    playerInstance?: PlayerManager;
    scale?: number;
    setScale?: () => void;
    lowLatency: boolean;
    onLowLatencyPress: () => void;
    onCastingPress: () => void;
    isCasting: boolean
}

const PlayerHeader = ({
    onBack,
    channelLogoUrl = '',
    title,
    style = {},
    streamType,
    onMiniPlayerPress,
    onInfoPress,
    onTvGuidePress,
    onLiveTvPress,
    // onLanguageSelectionPress,
    onLanguagePress,
    textTracks,
    audioTracks,
    selectedAudioTrack,
    item,
    currentTime,
    playerInstance,
    scale,
    setScale,
    lowLatency,
    onLowLatencyPress,
    onCastingPress,
    isCasting
}: PlayerHeaderProps): JSX.Element => {
    const { client, warningChannel, castSession, castDevice, continueWatchingChannel } = useChromecastContext();
    const { onPause } = useContinueWatching(item, () => null);
    const [liveForSyntethic, setLiveForSyntethic] = useState(false);
    // Could be change to object to check every feature
    const [isNewFeaturesChecked, setIsNewFeaturesChecked] = useState(false);
    const { deviceStorageDispatch, userData } = useStore();
    const isFreemium = isUserFreemium(userData);
    const { setModalContent } = useConfirmationModal();

    // watching display of "switch to live icon" for synthetic channels
    useEffect(() => {
        if (playerInstance) {
            // hack for diferentianing between synthetic channels and real ones
            if (!client && streamType === 'LIVE' && playerInstance?.live === false && (playerInstance.getUTCTimestamp(playerInstance.currentTime)?.getTime() < Date.now() - 30000)) {
                setLiveForSyntethic(true);
            } else {
                setLiveForSyntethic(false);
            }

        }
    }, [playerInstance?.currentTime, client]);

    // const stopChromecastSession = () => client?.stop();

    const onPauseGoBack = () => {
        const duration =
            (item && ('getStartTime' in item && 'getEndTime' in item ? item.getEndTime() - item.getStartTime() : null)) ||
            0;
        if (duration > 0 && currentTime) onPause(currentTime, Number(duration));
        // stopChromecastSession();
        onBack();
    };
    const closeModal = () => setModalContent(null);
    const onPressBack = () => {
        if (castSession && castDevice) {
            setModalContent(
                <ConfirmationModal
                    icon="warning_yellow_black"
                    header={t('COMMON.INFORMATION_MSG')}
                    message={t('SS.CHROMECAST.CANCEL_CASTING_MSG')}
                    onClose={closeModal}
                    mode='landscape'
                    confirmLabel={t('SS.CHROMECAST.CANCEL_CASTING_BACK_BTN')}
                    onConfirm={() => {
                        if (continueWatchingChannel) continueWatchingChannel.sendMessage(JSON.stringify({
                            message: 'CONTINUE WATCHING EVENT',
                            state: true
                        }));
                        closeModal();
                        onPauseGoBack();
                        client.stop();
                        if (warningChannel) warningChannel.sendMessage(JSON.stringify({
                            message: 'WARNING OUTSIDE OF PLAYER',
                            state: true
                        }));
                    }}
                    cancelLabel={t('SS.CHROMECAST.CANCEL_CASTING_CONTINUE_BTN')}
                    onCancel={closeModal}
                />
            );
        } else {
            onPauseGoBack();
        }
    };
    const onCastingButtonPress = () => {
        if (isFreemium) {
            if (Device.isAndroid) {
                logEvent({
                    type: 'user-flow',
                    subtype: 'player',
                    content1: 'maxiplayer',
                    content2: 'chromecast-forbidden-benefit',
                    context: '/player'
                });
                setModalContent(
                    <ConfirmationModal
                        header={Device.isAndroid ? t('SS.CHROMECAST.UNAVAILABLE_FOR_THIS_PACKAGE.TITLE') : t('SS.AIRPLAY.UNAVAILABLE_FOR_THIS_PACKAGE.TITLE')}
                        message={Device.isAndroid ? t('SS.CHROMECAST.UNAVAILABLE_FOR_THIS_PACKAGE.DESCRIPTION') : t('SS.AIRPLAY.UNAVAILABLE_FOR_THIS_PACKAGE.DESCRIPTION')}
                        confirmLabel={t('COMMON.OK_I_UNDERSTAND.BTN')}
                        onConfirm={closeModal}
                        mode='landscape'
                        onClose={closeModal}
                        headerStyle={{ alignItems: 'flex-end' }}
                        innerContainerStyle={{ width: '60%' }}
                    />
                );
            }
        } else {
            onCastingPress();
        }
    };

    useEffect(() => {
        const getDeviceConfigData = async () => {
            const deviceConfig = await appManager.getDeviceConfigData();
            setIsNewFeaturesChecked(deviceConfig.isNewFeaturesChecked);
        };
        getDeviceConfigData();
        const backAction = () => {
            onBack();
            return true;
        };

        const backHandler = BackHandler.addEventListener(
            'hardwareBackPress',
            backAction,
        );

        return () => backHandler.remove();
    }, []);

    const icons = useMemo(() => {
        const returnedIcons = [];
        if (!client && scale) {
            returnedIcons.push(
                <Icon
                    key="zoomIcon"
                    style={styles.icon}
                    name=""
                    testID="zoom_button"
                    onPress={() => setScale()}
                >
                    <View style={[styles.iconScale]}>
                        <Text style={styles.iconScaleText}>{`${scale ? scale.toFixed(2) : 1}x`}</Text>
                    </View>
                </Icon>
            );
        }
        if (item) {
            returnedIcons.push(
                <Icon
                    key="infoIcon"
                    style={styles.icon}
                    onPress={onInfoPress}
                    name="info"
                    testID="detail_button"
                />
            );
        }
        if (streamType === STREAM_TYPE.LIVE || streamType === STREAM_TYPE.ARCHIVE) {
            returnedIcons.push(
                <Icon
                    key="guideIcon"
                    style={styles.icon}
                    onPress={onTvGuidePress}
                    name="tv_guide"
                    testID="player_epg_button"
                />
            );
        }
        if (streamType === STREAM_TYPE.ARCHIVE || (liveForSyntethic)) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (item?.getMetaData()?.detail?.seekingForbidden) {
                if (!isLive(item)) {
                    returnedIcons.unshift(
                        <Icon
                            key="liveTv"
                            style={styles.icon}
                            onPress={onLiveTvPress}
                            name="live_tv"
                            testID="live_tv_button"
                        />
                    );
                }
            } else {
                returnedIcons.unshift(
                    <Icon
                        key="liveTv"
                        style={styles.icon}
                        onPress={onLiveTvPress}
                        name="live_tv"
                        testID="live_tv_button"
                    />
                );
            }

        }
        if (!client &&
            ((textTracks && textTracks?.length > 1) || (audioTracks && audioTracks.length > 1))
        ) {
            returnedIcons.push(
                <Icon
                    key="subtitlesIcon"
                    style={styles.icon}
                    onPress={onLanguagePress}
                    name="subtitles"
                    testID="subtitles_button"
                />
            );
        }
        if (lowLatency) {
            returnedIcons.push(
                isNewFeaturesChecked ?
                    <Icon
                        key="lowLatency"
                        style={{ ...styles.icon }}
                        onPress={onLowLatencyPress}
                        name="low_latency"
                        testID="low_latency_button"
                    /> :
                    // Animation with isNewFeaturesChecked could be passed right inside the Icon
                    <AnimatedCircle style={{ container: Device.isWeb ? { marginLeft: 20 } : undefined }}>
                        <Icon
                            key="lowLatency"
                            style={{
                                ...styles.icon,
                                ...styles.lastIcon,
                                ...(Device.isWeb ? { marginLeft: 0 } : undefined)
                            }}
                            onPress={() => {
                                deviceStorageDispatch({ type: storeActions.setIsNewFeaturesChecked, payload: true });
                                // setIsNewFeaturesChecked(true);
                                onLowLatencyPress();
                            }}
                            name="low_latency"
                            testID="low_latency_button"
                        />
                    </AnimatedCircle>
            );
        }
        if (item && Device.isAndroid) {
            returnedIcons.push(
                <CastingButton
                    onPress={onCastingButtonPress}
                    key="googleCastIcon"
                    style={{ ...styles.icon, ...styles.lastIcon }}
                    isCasting={isCasting}
                />
            );
        }

        return returnedIcons;
    }, [
        audioTracks,
        onInfoPress,
        // onLanguageSelectionPress,
        onLiveTvPress,
        onMiniPlayerPress,
        onLanguagePress,
        onTvGuidePress,
        selectedAudioTrack,
        streamType,
        textTracks,
        isNewFeaturesChecked,
        client
    ]);

    return (
        <View
            style={[
                styles.container,
                style,
                Device.isTabletOrWeb
                    ? {
                        alignSelf: 'center',
                        maxHeight: 50
                    }
                    : {}
            ]}
        >
            <View style={styles.backIconContainer}>
                <Icon
                    style={styles.backIcon}
                    onPress={onPressBack}
                    name="arrow_left"
                    testID="back_button"
                />
                <View style={styles.divider} />
            </View>
            {channelLogoUrl ? (
                <ImageBackground
                    source={{ uri: channelLogoUrl || '' }}
                    style={styles.channelIcon}
                />
            ) : null}
            <Text style={styles.title} numberOfLines={1} testID="playback_title">
                {title}
            </Text>
            <View
                style={[
                    styles.iconsContainer,
                    { flex: icons ? icons.length + 1 : 1 }
                ]}
            >
                {icons}
            </View>
        </View>
    );
};

export default PlayerHeader;
